import type { AxiosPromise } from 'axios';
import type * as openapi from '@/helpers/api/types/openapiOverrides';

import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  isAxiosError,
} from 'axios';
import * as Sentry from '@sentry/vue';
import { jwtDecode } from 'jwt-decode';

import { getControllerSignal } from '@/utils/abortController';
import { getStrArrParam } from '@/utils/api';
import { useAuthStore } from '@/stores/useAuthStore';
import store from '@/store';
// eslint-disable-next-line import-x/order -- oxfmt sorts bare '@/store' before '@/store/modules'
import { ModuleNotificationsItemsData } from '@/store/modules/notifications/notificationsTypes';
import { debouncedExpiredLogout } from '@/helpers/logouts';
import { sendError } from '@/helpers/sentry';
import {
  AddressBookEndpointParam,
  AddressBookLookupData,
} from '@/models/addressBook';
import {
  AirRoadDataParams,
  AirRoadGeneralJob,
  AirRoadGeneralListPayload,
  AirRoadGeneralPayload,
  AirRoadJobsRequestData,
  AirRoadUpdateParams,
} from '@/models/airRoad';
import { AuthData } from '@/models/auth';
import { JobDraftParams, JobDraftsListItem } from '@/models/draft';
import {
  GeneralBookingFormData,
  GeneralBookingProductData,
} from '@/models/generalBooking';
import { QuantListData, TQuantDataParams } from '@/models/jobQuants';
import {
  JobTicketAtricle,
  JobTicketDashboardResponse,
  JobTicketResponce,
} from '@/models/jobTicket';
import {
  AxiosStatusResponse,
  Division,
  FileCustom,
  IGetJobs,
  ILookUp,
  IPostRequest4Changes,
  JobArticleData,
  JobBookingJobData,
  LookupDriverFull,
  LookupPostcodeData,
  ModuleNotifications,
  ProductLocation,
  SpreadheetParams,
} from '@/models/main';
import {
  ReportRequestParams,
  ReportResponse,
  TomorrowReportData,
  TomorrowTobeSchedReportData,
} from '@/models/reports';
import {
  SchedulerBoardCreated,
  SchedulerBoardLogsData,
  SchedulerBoardsPayload,
  SchedulerSaveBoardResponse,
  SchedulerScheduledStopsList,
  SchedulerShift,
  SchedulerTravelTimeResponse,
  SchedulerUnscheduledStop,
} from '@/models/scheduler';

declare module 'axios' {
  // noinspection JSUnusedGlobalSymbols
  export interface AxiosRequestConfig {
    skipAuth?: boolean;
  }
}

export const api: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_APP_API,
});

api.interceptors.request.use(
  async (config) => {
    // Skip for logIn and refreshAccessToken requests
    if (config.skipAuth) {
      return config;
    }
    // you can pass signal to config to handle aborting requests by yourself
    if (!config.signal) {
      config.signal = getControllerSignal();
    }
    const token = await getValidAccessToken();
    // TODO
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    config.headers = config.headers ?? {};
    config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  /* eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors */
  async (err: unknown) => await Promise.reject(err),
);

api.interceptors.response.use(
  (response) => response,
  async (err: unknown) => {
    if (isAxiosError(err)) {
      if (err.response?.status === 401 && useAuthStore().isLoggedIn) {
        Sentry.addBreadcrumb({
          type: 'debug',
          message: `The session has expired. ${err.response.config.url}`,
          category: 'debug',
        });
        await debouncedExpiredLogout();
      } else {
        if (!axios.isCancel(err)) {
          sendError(err, 'api.interceptors.response');
        }
      }
    }
    /* eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors */
    return await Promise.reject(err);
  },
);

/**
 *
 * ** Token endpoints: **
 *
 */

/**
 * A variable to hold refresh token request to reduce race conditions
 */
let refreshTokenRequests: Promise<string> | null = null;

/**
 * Get valid AccessToken for the axios request interceptor
 */
async function getValidAccessToken(): Promise<string> {
  const authStore = useAuthStore();
  // get access token from the store
  let accessToken = authStore.authData.access;
  const now = Math.floor(Date.now() * 0.001);

  // If RefreshToken is not valid => LogOut procedure
  if (!authStore.isRefreshTokenValid(now).value) {
    await debouncedExpiredLogout();
  } else if (!authStore.isAccessTokenValid(now).value) {
    // check if we already have any refresh requests
    // create request and save it into variable
    if (refreshTokenRequests === null) {
      refreshTokenRequests = refreshAccessToken();
    }
    // resolve the request
    accessToken = await refreshTokenRequests;
    // erase the variable
    refreshTokenRequests = null;
  }
  return accessToken;
}

/**
 POST – Authentificates user and gives tokens
 * Initial login to get user's JWT
 * @param data
 * @constructor
 */
export const logIn = async (data: {
  username: string;
  password: string;
}): Promise<boolean> => {
  const authStore = useAuthStore();
  const response = await postToken(undefined, data, { skipAuth: true });
  // TODO
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (response?.status === 200) {
    const {
      permissions,
      exp,
      name,
      // TODO
      // @ts-expect-error
      customer_code: customerCode,
    }: AuthData = jwtDecode(response.data.access);
    const params = {
      refresh: response.data.refresh,
      access: response.data.access,
      permissions,
      exp,
      name,
      customerCode,
    };
    authStore.addAuthData(params);
    return true;
  }
  return false;
};

export const postToken = async (
  params: openapi.TokenCreateQueryOpenapi,
  data: openapi.TokenCreateBodyOpenapi,
  requestConfig: AxiosRequestConfig = {},
): AxiosPromise<openapi.TokenCreateContentOpenapi> => {
  return await api.post<openapi.TokenCreateContentOpenapi>('/token/', data, {
    ...requestConfig,
    params,
  });
};

export const postTokenRefresh = async (
  params: openapi.TokenRefreshCreateQueryOpenapi,
  data: openapi.TokenRefreshCreateBodyOpenapi,
  requestConfig: AxiosRequestConfig = {},
): AxiosPromise<openapi.TokenRefreshCreateContentOpenapi> => {
  return await api.post<openapi.TokenRefreshCreateContentOpenapi>(
    '/token/refresh/',
    data,
    {
      ...requestConfig,
      params,
    },
  );
};

/** POST token/refresh/ – Takes a refresh type JSON web token and returns an access
 * type JSON web token if the refresh token is valid.
 * Refresh AccessToken using valid RefreshToken
 */
async function refreshAccessToken(): Promise<string> {
  const authStore = useAuthStore();
  const response = await postTokenRefresh(
    undefined,
    { refresh: authStore.authData.refresh },
    { skipAuth: true },
  );
  authStore.updateAccessToken(response.data.access);
  return response.data.access;
}
/**
 *
 * // Token endpoints //
 *
 */

/**
 *
 * ** Common functions: **
 *
 */

// DEPRECATED, use standard approach with axious helpers/api/requests
// creating the string in '?&dates=2022-01-06,2022-01-13&customer_code=RICOH,CANON&state=NSW,NT,NZ' format
// TODO: type for random object except 'any'?
export function generateQueryString(
  data:
    | []
    | ILookUp
    | AirRoadDataParams
    | AirRoadGeneralListPayload
    | AddressBookEndpointParam
    | AirRoadJobsRequestData
    | ReportRequestParams
    | LookupPostcodeData,
): string {
  const query: string[] = [];
  /* eslint-disable-next-line @typescript-eslint/no-for-in-array */
  for (const key in data) {
    // TODO: add switch case
    // TODO
    // @ts-expect-error
    if (data[key] || typeof data[key] === 'boolean') {
      // TODO
      // @ts-expect-error
      query.push(`${key}=${encodeURIComponent(data[key])}`);
    }
  }
  return query.length > 0 ? '?' + query.join('&') : '';
}

/**
 *
 * // Common functions //
 *
 */

/**
 *
 * ** Common endpoints: **
 *
 */
export const getFileFromUrl = async (link: string): Promise<Blob> => {
  return (await api.get(link, { responseType: 'blob' })).data;
};

export const getVersion = async (): Promise<string> => {
  return (await getRootVersionJson()).data.version;
};
export const getRootVersionJson = async (
  requestConfig: AxiosRequestConfig = {},
): Promise<AxiosResponse> => {
  return await api.get('/version.json', {
    ...requestConfig,
    baseURL: 'https://' + window.location.hostname,
  });
};
/**
 *
 * // Common endpoints //
 *
 */

/**
 *
 * ** Profile: **
 *
 */
export const getUserProfile = async (
  params: openapi.UserProfileReadQueryOpenapi,
  requestConfig: AxiosRequestConfig = {},
): AxiosPromise<openapi.UserProfileReadContentOpenapi> => {
  return await api.get<openapi.UserProfileReadContentOpenapi>(
    '/user/profile/',
    {
      ...requestConfig,
      params,
    },
  );
};

export const putUserProfile = async (
  params: openapi.UserProfileUpdateQueryOpenapi,
  data: FormData, // TODO as openapi.UserProfileUpdateBodyOpenapi
  requestConfig: AxiosRequestConfig = {},
): AxiosPromise<openapi.UserProfileUpdateContentOpenapi> => {
  return await api.putForm<openapi.UserProfileUpdateContentOpenapi>(
    '/user/profile/',
    data,
    {
      ...requestConfig,
      params,
    },
  );
};
/**
 *
 * // Profile //
 *
 */

/**
 *
 * ** Dashboard and dashboard like tables: **
 *
 */

// TODO string[], boolean in template strings, getStrArrParam

//  path('api/dashboard/bfixjobs/', bfix_dashboard.BFIXDashboardView.as_view(), name='dashboard-bfixjobs'),
export const getBfixJobs = async (dateFilter: string[]): Promise<IGetJobs> => {
  return (await api.get(`dashboard/bfixjobs/?date_filter=${dateFilter}`)).data;
};

// POST /dashboard/job/note/ – API endpoint for dashboard which creates note object
export const postJobEdit = async (
  params: Record<string, unknown>,
  user: string,
): Promise<void> => {
  const response = await api.post('dashboard/job/note/', { params });
  if (response.status === 200) {
    await store.dispatch('addNote', { created_by: user, ...params });
  }
};

/**
 *
 * // Dashboard and dashboard like tables //
 *
 */

/**
 *
 * ** Job related: **
 *
 */

// POST /job/rq4chgs/ – Request for change a job
export const postRequest4Changes = async (
  params: IPostRequest4Changes,
): Promise<AxiosResponse> => {
  return await api.post('job/rq4chgs/', params);
};

// GET /job/pod/ – Returns proof of delivery image by job's stop
export const getPOD = async (
  jobId: number,
  jobDate: string,
  stopNumber: number,
): Promise<string> => {
  return await api
    .get(
      `job/pod/?job_id=${jobId}&job_date=${jobDate}&stop_number=${stopNumber}`,
      {
        responseType: 'blob',
        // We need to override default validateStatus function to allow 404 status code and return string for error path.
        validateStatus: (status: number) => status === 200 || status === 404,
      },
    )
    .then((res) => window.URL.createObjectURL(new Blob([res.data])))
    .catch(() => 'Not found');
};

// POST /job/book_b2b_xls/ – Books B2B jobs from XLS-file
export const uploadB2BJobsSpreadsheet = async (
  file: FileCustom,
  params: SpreadheetParams,
  serviceCode: string,
): Promise<AxiosResponse> => {
  const filename = file.filename;
  const bodyFormData = new FormData();
  bodyFormData.append('file', file);
  return await api.post(
    `job/book_b2b_xls/?filename=${filename}&dry_run=${params.dry_run}&service_code=${serviceCode}`,
    bodyFormData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  );
};

// POST /job/book_api/ – Books jobs from web API
// res.status === 201
export const bookJobAdvanced = async (
  params: JobBookingJobData[],
): Promise<AxiosResponse> => {
  return await api.post('job/book_api/?dry_run=false', params);
};

// GENERAL BOOKING
// POST /job/book_3pl/ – Books jobs from web API - General booking
export const bookJobGeneral = async (
  params: GeneralBookingFormData[],
): Promise<AxiosResponse> => {
  return await api.post('job/book_3pl/?dry_run=false', params);
};

// Products list:
export const getProductsGeneral = async (): Promise<
  GeneralBookingProductData[]
> => {
  return (await api.get('/job/product/general/')).data;
};

// GET Zammad attachments
export const getJobTicketAttachment = async (
  ticketId: number,
  articleId: number,
  fileId: number,
): Promise<AxiosResponse> => {
  return await api.get(
    `/job/article-attachment/?ticket_id=${ticketId}&article_id=${articleId}&attachment_id=${fileId}`,
    {
      responseType: 'blob',
    },
  );
};

// res.status === 200
export const syncJobs = async (): Promise<AxiosResponse> => {
  return await api.get('job/daysync/');
};
/**
 *
 * // Job related //
 *
 */

/**
 *
 * ** Common lookups: **
 *
 */

// GET /lookup/suburb/ – Look up Suburb which name starts with suburb_guess
export const getLookupSuburb = async (
  val: string,
  requestConfig: AxiosRequestConfig = {},
): Promise<AxiosResponse> => {
  return await api.get(`/lookup/suburb/?suburb_guess=${val}`, requestConfig);
};

export const getLookupCustomer = async (
  params: openapi.LookupCustomerListQueryOpenapi,
  requestConfig: AxiosRequestConfig = {},
): AxiosPromise<openapi.LookupCustomerListContentOpenapi> => {
  return await api.get<openapi.LookupCustomerListContentOpenapi>(
    '/lookup/customer/',
    {
      ...requestConfig,
      params,
    },
  );
};

export const getLookupDriverByJob = async (
  params: openapi.LookupDriverByJobOpenapi,
  requestConfig: AxiosRequestConfig = {},
): Promise<AxiosResponse> => {
  return await api.get('/lookup/driver_by_job/', {
    ...requestConfig,
    params,
  });
};

export const getLookupService = async (
  params: openapi.LookupServiceListOpenapi,
  requestConfig: AxiosRequestConfig = {},
): Promise<AxiosResponse> => {
  return await api.get('/lookup/service/', {
    ...requestConfig,
    params,
  });
};

// GET /lookup/driver/ – (+) manually edited
export const getDriversLookup = async (
  driver: string,
): Promise<LookupDriverFull[]> => {
  return (await api.get(`/lookup/driver/?driver=${driver}`)).data;
};

// GET /lookup/product/model/ – Lookup for product model with product_id and model/alias name
export const getLookupProductModel = async (
  val: string,
  requestConfig: AxiosRequestConfig = {},
): Promise<AxiosResponse<openapi.LookupProductModelContentOpenapi>> => {
  return await api.get<openapi.LookupProductModelContentOpenapi>(
    `/lookup/product/model/?model=${encodeURIComponent(val)}`,
    requestConfig,
  );
};
export const getProductModel = async (
  model: string,
): Promise<openapi.LookupProductModelContentOpenapi> => {
  return (await getLookupProductModel(model)).data;
};

// GET /lookup/product/finisher/ – Lookup for finisher model with product_id and model name
export const getLookupProductFinisher = async (
  model: string,
  customer: string,
  requestConfig: AxiosRequestConfig = {},
): Promise<AxiosResponse<openapi.LookupProductFinisherContentOpenapi>> => {
  return await api.get<openapi.LookupProductFinisherContentOpenapi>(
    `/lookup/product/finisher/?model=${encodeURIComponent(model)}&customer=${encodeURIComponent(customer)}`,
    requestConfig,
  );
};

// GET /lookup/product/location/ – Lookup for product location in ODOO
export const getProductLocation = async (
  productId: number,
  serial: string,
  requestConfig: AxiosRequestConfig = {},
): Promise<AxiosResponse<ProductLocation>> => {
  return await api.get<ProductLocation>(
    `lookup/product/location/?product_id=${productId}&serial=${encodeURIComponent(
      serial,
    )}`,
    requestConfig,
  );
};

// GET /lookup/address/ – Lookup for addresses of client
export const getAddressBook = async (
  customer: string,
): Promise<AddressBookLookupData[]> => {
  return (await api.get(`lookup/address/?customer=${customer}`)).data;
};

// Get list of divisions
export const getDivisions = async (): Promise<Division[]> => {
  return (await api.get('lookup/division/')).data;
};

/**
 *
 * // Common lookups //
 *
 */

export interface PostDriverStopArriveParams {
  driver_id: number;
  job_id: number;
  job_date: string;
  stop_number: number;
  arrival_time?: string;
}

// Sends signal to RH about driver arrived at Stop
export const postDriverStopArrive = async (
  params: PostDriverStopArriveParams,
  requestConfig: AxiosRequestConfig = {},
): Promise<AxiosResponse> => {
  return await api.post('/driver/stop/arrive/', params, requestConfig);
};

export interface PostDriverStopDepartParams {
  driver_id: number;
  job_id: number;
  job_date: string;
  stop_number: number;
  departure_time?: string;
  pod_name?: string;
}

// Sends signal to RH about driver departed Stop
export const postDriverStopDepart = async (
  params: PostDriverStopDepartParams,
  requestConfig: AxiosRequestConfig = {},
): Promise<AxiosResponse> => {
  return await api.post('/driver/stop/depart/', params, requestConfig);
};

/**
 *
 * ** Reports: **
 *
 */

// POST /report/bulksignatures/ – Generates ZIP archive with POD signatures
// file
export const postBulkSignature = async (
  params: Record<'refs', string[]>,
): Promise<AxiosResponse> => {
  return await api.post('report/bulksignatures/', params, {
    responseType: 'blob',
  });
};

// GET /report/runsheet/params/ – Returns dict with list of drivers on date, for user to choose
export const getRunSheetList = async (
  params: {
    states: string[];
    dates: string[];
  },
  requestConfig: AxiosRequestConfig = {},
): Promise<AxiosResponse> => {
  const states = getStrArrParam(params.states);
  const dates = getStrArrParam(params.dates);
  return await api.get(
    `report/runsheet/params/?states=${states}&dates=${dates}`,
    requestConfig,
  );
};

// POST /report/runsheet/ – Returns RunSheet report in PDF format
// file
export const getRunSheetPDFs = async (
  params: [string, number][],
): Promise<AxiosResponse> => {
  return await api.post('report/runsheet/', params, { responseType: 'blob' });
};

// GET /report/eta/ – Generate and returns ETA report in JSON/CSV/PDF formats
// List
export const getReportEta = async (
  // TODO
  // @ts-expect-error
  params,
  requestConfig: AxiosRequestConfig = {},
): Promise<AxiosResponse> => {
  const queryString = generateQueryString(params);
  return await api.get(`/report/eta/${queryString}`, requestConfig);
};

// file downloading
export const getReportEtaFile = async (
  // TODO
  // @ts-expect-error
  params,
  requestConfig: AxiosRequestConfig = {},
): Promise<AxiosResponse> => {
  const queryString = generateQueryString(params);
  return await api.get(`/report/eta/${queryString}`, {
    ...requestConfig,
    responseType: 'blob',
  });
};

// POST /report/eta/ – Generates and sends e-mail with ETA report in CSV format to list of customer's e-mails
// res.status === 200
export const postReportEta = async (
  // TODO
  // @ts-expect-error
  params,
  requestConfig: AxiosRequestConfig = {},
): Promise<AxiosResponse> => {
  const queryString = generateQueryString(params);
  return await api.post(`/report/eta/${queryString}`, requestConfig);
};

// A general endpoint to get different Report types
export const getReports = async (
  params: ReportRequestParams,
  requestConfig: AxiosRequestConfig = {},
): Promise<AxiosResponse> => {
  const queryString = generateQueryString(params);
  return await api.get(`/reports/${queryString}`, requestConfig);
};
export const getReportList = async (
  params: ReportRequestParams,
): Promise<ReportResponse[]> => {
  return (await getReports(params)).data;
};

export const postReportsGenerateReport = async (
  params: ReportRequestParams,
  requestConfig: AxiosRequestConfig = {},
): Promise<AxiosResponse> => {
  const queryString = generateQueryString(params);
  return await api.post(
    `/reports/generate_report/${queryString}`,
    requestConfig,
  );
};
export const generateReport = async (
  params: ReportRequestParams,
): Promise<string> => {
  return (await postReportsGenerateReport(params)).data;
};

export const getReportFile = async (
  reportId: number,
  requestConfig: AxiosRequestConfig = {},
): Promise<AxiosResponse> => {
  return await api.get(`/reports/${reportId}/download/`, {
    ...requestConfig,
    responseType: 'blob',
  });
};
export const getTomorrowReportFile = async (
  date: string,
  division: string,
): Promise<AxiosResponse> => {
  return await api.get(
    `report/tomorrow/?report_date=${date}&division=${division}`,
    { responseType: 'blob' },
  );
};
export const getTomorrowReportShedData = async (
  date: string,
  division: string,
): Promise<TomorrowReportData[]> => {
  return (
    await api.get(
      `report/tomorrow-sched/?report_date=${date}&division=${division}`,
    )
  ).data;
};
export const getTomorrowReportSchedPDF = async (
  date: string,
  division: string,
): Promise<AxiosResponse> => {
  return await api.get(
    `report/tomorrow-sched-pdf/?report_date=${date}&division=${division}`,
    {
      responseType: 'blob',
    },
  );
};
export const getTomorrowReportTobeSchedData = async (
  date: string,
  division: string,
): Promise<TomorrowTobeSchedReportData[]> => {
  return (
    await api.get(
      `report/tomorrow-tobesched/?report_date=${date}&division=${division}`,
    )
  ).data;
};
export const getTomorrowReportTobeSchedPDF = async (
  date: string,
  division: string,
): Promise<AxiosResponse> => {
  return await api.get(
    `report/tomorrow-tobesched-pdf/?report_date=${date}&division=${division}`,
    {
      responseType: 'blob',
    },
  );
};
export const getTomorrowReportSplitData = async (
  date: string,
  division: string,
): Promise<TomorrowReportData[]> => {
  return (
    await api.get(
      `report/tomorrow-split/?report_date=${date}&division=${division}`,
    )
  ).data;
};

export const getReportWeeklyStat = async (
  params: {
    states: string[];
    dates: string[];
  },
  requestConfig: AxiosRequestConfig = {},
): Promise<AxiosResponse> => {
  const states = getStrArrParam(params.states);
  const dates = getStrArrParam(params.dates);
  return await api.get(
    `/report/weekly_stat/?states=${states}&dates=${dates}`,
    requestConfig,
  );
};

export const getAmPmJobs = async (
  date: string,
  requestConfig: AxiosRequestConfig = {},
): Promise<AxiosResponse> => {
  return await api.get(`/report/am_pm_jobs/?date=${date}`, requestConfig);
};

/**
 *
 * // Reports //
 *
 */

/**
 *
 * **AirRoad: **
 *
 */

export const sendQuickJobsAToirRoad = async (
  ids: number[],
): Promise<AxiosResponse> => {
  return await api.post('/airroad/booking/send_bulk/', ids);
};

// GET /airroad/lookup_locations/ Returns locations of Airroad
export const getAirRoadLocations = async (): Promise<[]> => {
  return (await api.get('airroad/lookup_locations/')).data;
};

// POST /airroad/populate_with_data/ Populates job with a data from a product related to stop
// res.status === 200
export const populateAirRoadJobs = async (
  ids: number[],
): Promise<AxiosResponse> => {
  const formattedParams = {
    job_ids: ids,
  };
  return await api.post('/airroad/populate_with_data/', formattedParams);
};

// POST /airroad/send_to_ar/ Queues jobs from provided list to be sent to AirRoad
// response is not using
export const sendJobToAirRoad = async (
  ids: number[],
): Promise<AxiosResponse> => {
  const formattedParams = {
    job_ids: ids,
  };
  return await api.post('/airroad/send_to_ar/', formattedParams);
};

// POST /airroad/revert_to_not_booked/ Returns back to 'Not booked' status job that was booked with error previously
// res.status === 200
export const revertAirRoadJobToNotBooked = async (
  ids: number[],
): Promise<AxiosResponse> => {
  const formattedParams = {
    job_ids: ids,
  };
  return await api.post('/airroad/revert_to_not_booked/', formattedParams);
};

// PUT /airroad/{id}/
// not using resp at all
export const updateAirRoadJobData = async (
  id: number,
  params: AirRoadUpdateParams,
): Promise<AxiosResponse> => {
  return await api.put(`/airroad/${id}/`, params);
};

// GET /airroads/3rdparty/ Lists all jobs related to interstate transportation for further booking AR services
export const getAirRoad3rdparty = async (
  date: string,
  hasAirRoadJob?: boolean,
  id?: number,
): Promise<AirRoadGeneralJob[]> => {
  const params: AirRoadGeneralListPayload = {
    rh_date: date,
    has_ar_job: hasAirRoadJob ?? false,
    rh_id: id,
  };
  const queryString = generateQueryString(params);
  return (await api.get(`/airroads/3rdparty/${queryString}`)).data;
};

// POST /airroads/3rdparty/ Makes AR booking for provided stops
// res.status === 201
export const sendAirRoad3rdparty = async (
  params: AirRoadGeneralPayload[],
): Promise<AxiosResponse> => {
  return await api.post('/airroads/3rdparty/', params);
};

// PUT /airroads/3rdparty/{id}/ Update AR-related fields in any job
// () – not using data or response
export const updateAirRoad3rdpartyJobData = async (
  id: number,
  params: AirRoadUpdateParams,
): Promise<AxiosResponse> => {
  return await api.put(`/airroads/3rdparty/${id}/`, params);
};

/**
 *
 * // AirRoad //
 *
 */

/**
 *
 * ** Scheduler: **
 *
 */

// GET /sched/shift/ – (+) manually edited
export const getSchedulerShifts = async (
  date: string,
): Promise<SchedulerShift[]> => {
  return (await api.get(`/sched/shift/?shift_date=${date}`)).data;
};

// GET /sched/shift/{id}/ – (+) manually edited
export const getSchedulerShift = async (
  id: number,
): Promise<SchedulerShift> => {
  return (await api.get(`/sched/shift/${id}/`)).data;
};

// PUT /sched/shift/{id}/ – (+) manually edited
export const putSchedulerShift = async (
  id: number,
  params: SchedulerShift,
): Promise<SchedulerShift> => {
  return (await api.put(`/sched/shift/${id}/`, params)).data;
};

// POST /sched/shift/copy_from_date/ – (+) manually edited
export const getSchedulerShiftFromDate = async (
  dateFrom: string,
  dateTo: string,
): Promise<SchedulerShift[]> => {
  return (
    await api.post(
      `/sched/shift/copy_from_date/?from_date=${dateFrom}&to_date=${dateTo}`,
    )
  ).data;
};

export const getSchedulerShiftFromLast = async (
  dateTo: string,
): Promise<SchedulerShift[]> => {
  return (await api.post(`/sched/shift/copy_from_last/?to_date=${dateTo}`))
    .data;
};

export const getSchedulerGenerateShift = async (
  dateTo: string,
): Promise<SchedulerShift[]> => {
  return (await api.post(`/sched/shift/generate_shift/?to_date=${dateTo}`))
    .data;
};

/**
 *
 * // Scheduler //
 *
 */

/**
 *
 * ** Drafts: **
 *
 */

// POST /job/draft/ – Creates draft
// res.status === 201
export const createDraft = async (
  data: JobDraftParams,
): Promise<AxiosResponse> => {
  return await api.post('job/draft/', data);
};

// GET /job/draft/{id}/ – Returns job draft with additional info
export const getDraft = async (
  id: number,
  requestConfig: AxiosRequestConfig = {},
): Promise<JobDraftsListItem> => {
  return (await api.get(`job/draft/${id}/`, requestConfig)).data;
};

// PUT /job/draft/{id}/ – Updates job draft
// res.status === 200
export const editDraft = async (
  id: number,
  data: JobDraftParams,
  requestConfig: AxiosRequestConfig = {},
): Promise<AxiosResponse> => {
  return await api.put(`job/draft/${id}/`, data, requestConfig);
};

// DELETE /job/draft/{id}/ – Deletes job draft
// res.status === 204
export const deleteDraft = async (
  id: number,
  requestConfig: AxiosRequestConfig = {},
): Promise<AxiosResponse> => {
  return await api.delete(`/job/draft/${id}/`, requestConfig);
};
/**
 *
 * // Drafts //
 *
 */

export const getNews = async (
  params: openapi.NewsListQueryOpenapi,
  requestConfig: AxiosRequestConfig = {},
): Promise<AxiosResponse> => {
  return await api.get('/news/', {
    ...requestConfig,
    params,
  });
};

export const getSchedulerStops = async (
  board: number,
): Promise<SchedulerUnscheduledStop[]> => {
  return (await api.get(`sched/board/${board}/stops_for_board/`)).data;
};

export const getSchedulerTravelTime = async (
  suburb1: number,
  suburb2: number,
): Promise<SchedulerTravelTimeResponse> => {
  const travelTimeResponse = await api.get(
    `sched/travel_time/?suburb1=${suburb1}&suburb2=${suburb2}`,
  );
  return travelTimeResponse.data;
};

export const getSchedulerScheduledStops = async (
  id: number,
): Promise<SchedulerScheduledStopsList[]> => {
  return (await api.get(`sched/board/${id}/list_schedules/`)).data;
};

export const getSchedulerBoard = async (
  id: number,
): Promise<SchedulerBoardCreated> => {
  return (await api.get(`/sched/board/${id}/`)).data;
};

export const getSchedulerBoards = async (
  params: SchedulerBoardsPayload,
): Promise<SchedulerBoardCreated[]> => {
  const payload = Object.keys(params)
    // TODO
    // @ts-expect-error

    .map((key) => key + '=' + params[key])
    .join('&');
  return (await api.get(`/sched/board/?${payload}`)).data;
};

export const lockSchedulerBoard = async (
  id: number,
): Promise<SchedulerBoardCreated> => {
  return (await api.post(`/sched/board/${id}/lock/`)).data;
};

export const grabSchedulerBoard = async (
  id: number,
): Promise<SchedulerBoardCreated> => {
  return (await api.post(`/sched/board/${id}/grab/`)).data;
};

export const unlockSchedulerBoard = async (
  id: number,
): Promise<SchedulerBoardCreated> => {
  return (await api.post(`/sched/board/${id}/unlock/`)).data;
};

export const createSchedulerBoard = async (
  params: SchedulerBoardsPayload,
): Promise<SchedulerBoardCreated> => {
  return (await api.post('/sched/board/', params)).data;
};

export const saveSchedulerBoardStops = async (
  id: number,
  params: SchedulerScheduledStopsList[],
): Promise<SchedulerSaveBoardResponse> => {
  return (await api.post(`sched/board/${id}/save_schedules/`, params)).data;
};

export const commitSchedulerBoard = async (
  id: number,
): Promise<AxiosResponse> => {
  return (await api.post(`sched/board/${id}/commit/`)).data;
};

export const getSchedulerBoardLogs = async (
  id: number,
): Promise<SchedulerBoardLogsData[]> => {
  return (await api.get(`/sched/board_log/?board=${id}`)).data;
};

// Allocate / deallocate stops, trucks, boards
// Stops
export const allocateSchedulerStop = async (
  scheduleId: number,
  boardId: number,
  withFinalLock: boolean,
): Promise<AxiosResponse> => {
  return (
    await api.post(
      `sched/alloc/stop/allocate/?schedule=${scheduleId}&board=${boardId}&with_final_lock=${withFinalLock}`,
    )
  ).data;
};

export const deAllocateSchedulerStop = async (
  scheduleId: number,
  boardId: number,
  withFinalLock: boolean,
): Promise<AxiosResponse> => {
  return (
    await api.post(
      `sched/alloc/stop/deallocate/?schedule=${scheduleId}&board=${boardId}&with_final_lock=${withFinalLock}`,
    )
  ).data;
};

// Trucks
export const allocateSchedulerTruck = async (
  truck: string,
  boardId: number,
  withFinalLock: boolean,
): Promise<AxiosResponse> => {
  return (
    await api.post(
      `sched/alloc/truck/allocate/?truck=${truck}&board=${boardId}&with_final_lock=${withFinalLock}`,
    )
  ).data;
};

export const deAllocateSchedulerTruck = async (
  truck: string,
  boardId: number,
  withFinalLock: boolean,
): Promise<AxiosResponse> => {
  return (
    await api.post(
      `sched/alloc/truck/deallocate/?truck=${truck}&board=${boardId}&with_final_lock=${withFinalLock}`,
    )
  ).data;
};

// Boards
export const allocateSchedulerBoard = async (
  boardId: number,
  withFinalLock: boolean,
): Promise<AxiosResponse> => {
  return (
    await api.post(
      `sched/alloc/board/allocate/?board=${boardId}&with_final_lock=${withFinalLock}`,
    )
  ).data;
};

export const deAllocateSchedulerBoard = async (
  boardId: number,
  withFinalLock: boolean,
): Promise<AxiosResponse> => {
  return (
    await api.post(
      `sched/alloc/board/deallocate/?board=${boardId}&with_final_lock=${withFinalLock}`,
    )
  ).data;
};

export const getJobTicket = async (
  jobId: number,
): Promise<JobTicketResponce> => {
  return (await api.get(`job/tickets/?ticket_id=${jobId}`)).data;
};

export const editJobTicket = async (
  data: JobTicketResponce,
): Promise<JobTicketResponce> => {
  return (await api.put('job/tickets/', data)).data;
};

export const getJobArticles = async (
  ticketId: number,
): Promise<JobTicketAtricle[]> => {
  return (await api.get(`job/articles/?ticket_id=${ticketId}`)).data;
};

export const createJobArticle = async (
  article: JobArticleData,
): Promise<JobTicketAtricle> => {
  return (await api.post('job/articles/', article)).data;
};

export const editJobArticle = async (
  article: JobArticleData,
): Promise<JobTicketAtricle> => {
  return (await api.put('job/articles/', article)).data;
};

// Postcode Search Lookup
export const lookupPostcode = async (
  postcode: number,
): Promise<openapi.SuburbOpenapi[]> => {
  return (await api.get(`/lookup/postcode/?postcode=${postcode}`)).data;
};

// Notifications
export const getNotifications = async (
  unreadFilter: boolean,
  category: string | null,
  page?: number,
): Promise<ModuleNotifications> => {
  let queryString = '/notifications/';
  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
  unreadFilter ? (queryString += 'unread_list/') : (queryString += 'all_list/');
  queryString += `?page=${page}`;
  if (category) {
    queryString += `&category=${category}`;
  }
  return (await api.get(queryString)).data;
};

export const getNotificationsAllCount = async (): Promise<number> => {
  return (await api.get('/notifications/all_count/')).data;
};

export const getNotificationsUnreadCount = async (): Promise<number> => {
  return (await api.get('/notifications/unread_count/')).data;
};

export const getTicketsNotificationsUnreadCount = async (): Promise<number> => {
  return (await api.get('/ticket/dashboard/unread_count')).data;
};

export const getNotificationsAllList = async (): Promise<
  ModuleNotifications[]
> => {
  return (await api.get('/notifications/all_list/')).data;
};

export const getNotificationsUnreadList = async (): Promise<AxiosResponse> => {
  return await api.get('/notifications/unread_list/');
};

export const setNotificationAsUnreadRequest = async (
  id: number,
): Promise<AxiosResponse> => {
  return await api.post(`/notifications/notifications/${id}/mark_as_unread/`);
};

export const setNotificationsAllAsReadRequest =
  async (): Promise<AxiosResponse> => {
    return await api.post('/notifications/notifications/mark_all_as_read/');
  };

export const setNotificationAsReadRequest = async (
  id: number,
): Promise<AxiosResponse> => {
  return await api.post(`/notifications/notifications/${id}/mark_as_read/`);
};

export const deleteNotification = async (
  id: number,
): Promise<AxiosResponse> => {
  return await api.delete(`notifications/notifications/${id}/`);
};

export const setTicketNotificationAsReadRequest = async (
  id: number,
): Promise<AxiosResponse> => {
  return await api.post(
    `/ticket/notifications/mark_all_as_read?ticket_id=${id}`,
  );
};

export const getNotificationsAllSorted =
  async (): Promise<ModuleNotificationsItemsData> => {
    return (await api.get('/notifications/all_sorted/')).data;
  };

export const getUnreadTicketsDahboard = async (
  // TODO
  // @ts-expect-error
  showAll,
  // TODO
  // @ts-expect-error
  params,
): Promise<JobTicketDashboardResponse> => {
  const queryString = generateQueryString(params);
  return (
    await api.get(
      `/ticket/dashboard/${showAll ? 'all' : 'unread'}/${queryString}`,
    )
  ).data;
};

// GET /odoo/customer/quants/ - enpoint for fetchData in Quant List Page
export const getQuantListData = async (
  params: TQuantDataParams,
): Promise<QuantListData> => {
  return (await api.get(`/odoo/customer/quants/${generateQueryString(params)}`))
    .data;
};

export const setJobCustomerDetails = async (
  // TODO
  // @ts-expect-error
  params,
): Promise<AxiosStatusResponse> => {
  return (await api.put('/jobedit/customer-details/', params)).data;
};

export const getFilters = async (
  params: openapi.GetFilters_Query,
  requestConfig: AxiosRequestConfig = {},
): AxiosPromise<openapi.GetFilters_200> => {
  return await api.get<openapi.GetFilters_200>('/filters/', {
    ...requestConfig,
    params,
  });
};

export const postFilters = async (
  params: openapi.PostFilters_Query,
  data: openapi.PostFilters_Body,
  requestConfig: AxiosRequestConfig = {},
): AxiosPromise<openapi.PostFilters_201> => {
  return await api.post<openapi.PostFilters_201>('/filters/', data, {
    ...requestConfig,
    params,
  });
};

export const deleteFiltersById = async (
  params: openapi.DeleteFiltersById_Query,
  path: openapi.DeleteFiltersById_Path,
  requestConfig: AxiosRequestConfig = {},
): AxiosPromise<openapi.DeleteFiltersById_204> => {
  return await api.delete<openapi.DeleteFiltersById_204>(
    `/filters/${path.id}/`,
    {
      ...requestConfig,
      params,
    },
  );
};
